// eslint-disable-next-line no-unused-vars
class GA4Datalayer {
	constructor(dataLayer) {
		this.dataLayer = dataLayer;
		this.useGlobalId = false;
		this.batchSize = 30;

		this.events = {
			selectItem: {
				selectors: ["[data-layer='select_item']"],
				func: GA4Datalayer.addEvents.bind(this, { action: "click", callback: this.pushSelectItem.bind(this) }),
			},
			viewItemList: {
				selectors: ["[data-layer='view_item_list']", "[data-layer='view_item_list-slider-row']"],
				func: this.pushItemList.bind(this),
			},
			beginCart: {
				selectors: ["[data-layer='view_item'] #form-submit"],
				func: GA4Datalayer.addEvents.bind(this, {
					action: "click",
					callback: this.pushBeginCart.bind(this),
				}),
			},
		};

		this.init();
	}

	static addEvents(event, targets) {
		targets.forEach((target) => {
			target.addEventListener(event.action, event.callback);
		});
	}

	static arraySplit(items, chunkSize) {
		if (!chunkSize || chunkSize <= 0) return [items];
		const chunks = [];
		for (let i = 0; i < items.length; i += chunkSize) {
			chunks.push(items.slice(i, i + chunkSize));
		}
		return chunks;
	}

	formatItemId(globalId) {
		if (this.useGlobalId) return globalId;
		return globalId.replace(/\D+/g, "");
	}

	pushLogState(customerId) {
		// User ID tracking
		this.dataLayer.push({
			logState: customerId ? "Logged In" : "Logged Out",
			...(customerId && { userId: this.formatItemId(customerId) }),
		});
	}

	pushItemList(listNodes) {
		if (!Array.isArray(listNodes) || listNodes.length === 0) return;

		listNodes.forEach((list) => {
			const listName = list.getAttribute("data-list-name") || "undefined";
			const listItems = Array.from(list.querySelectorAll("article"));

			// Remove items like collections from productSliderRow
			const filteredItems = listItems.filter((item) => item.getAttribute("data-id"));

			// send in chunks
			GA4Datalayer.arraySplit(filteredItems, this.batchSize).forEach((items, chunckIndex) => {
				const payload = [];
				items.forEach((item, index) => {
					const { dataset } = item;
					payload.push({
						sku: dataset.sku,
						item_id: this.formatItemId(dataset.id),
						item_name: dataset.title,
						item_category: dataset.category,
						item_variant: dataset.variant,
						item_list_name: listName,
						price: parseFloat(dataset.price),
						index: this.batchSize * chunckIndex + index,
						quantity: 1,
					});
				});

				this.dataLayer.push({ ecommerce: null });
				this.dataLayer.push({
					event: "view_item_list",
					ecommerce: {
						currency: "GBP",
						items: payload,
					},
				});
			});
		});
	}

	pushSelectItem({ currentTarget }) {
		if (!currentTarget) return;

		const { dataset } = currentTarget;
		const itemList = currentTarget.closest("[data-list-name]");
		const index = Array.from(currentTarget.parentNode.querySelectorAll("article")).findIndex(
			(item) => item.getAttribute("data-id") === dataset.id
		);

		this.dataLayer.push({ ecommerce: null });
		this.dataLayer.push({
			event: "select_item",
			ecommerce: {
				currency: "GBP",
				value: parseFloat(dataset.price),
				items: [
					{
						sku: dataset.sku,
						item_id: this.formatItemId(dataset.id),
						item_name: dataset.title,
						item_category: dataset.category,
						item_variant: dataset.variant,
						item_list_name: itemList ? itemList.getAttribute("data-list-name") : "undefined",
						price: parseFloat(dataset.price),
						index,
						quantity: 1,
					},
				],
			},
		});
	}

	pushBeginCart() {
		const hasCardSelector = document.getElementById("card-save");
		if (hasCardSelector && !this.started) {
			this.dataLayer.push({
				event: "begin_cart",
			});
			// avoids sending multiple events when closing the selector without completing
			this.started = true;
		}
	}

	dispatchCheckoutChanges(oldItems, newItems) {
		// Check the difference between stored checkout items(oldItems) and
		// new checkout items(newItems) to find changes and send events
		try {
			if (oldItems) {
				if (!newItems && oldItems) {
					this.pushItems(oldItems, "remove_from_cart");
				} else {
					const itemsToRemove = oldItems.filter(
						(cartItem) => !newItems.find((newItem) => newItem.merchandise.id === cartItem.merchandise.id)
					);
					const itemsToAdd = newItems.filter(
						(newItem) => !oldItems.find((cartItem) => cartItem.merchandise.id === newItem.merchandise.id)
					);

					if (itemsToRemove.length > 0) this.pushItems(itemsToRemove, "remove_from_cart");

					if (itemsToAdd.length > 0) this.pushItems(itemsToAdd, "add_to_cart");
				}
			}
		} catch (error) {
			console.log("Error on dispatch datalayer", error);
		}
	}

	pushItems(nodes, eventName, extras, includeValue = true) {
		try {
			const payload = {
				event: eventName,
				ecommerce: {
					...(includeValue && { currency: "GBP", value: 0 }),
					...(extras && { ...extras }),
					items: [],
				},
			};

			nodes
				.filter((item) => Object.keys(item).length !== 0) // remove empty objects
				.forEach((node) => {
					const price = parseFloat(node.merchandise.price.amount);
					if (node.merchandise.id) {
						payload.ecommerce.items.push({
							item_name: node.merchandise.product.title,
							item_id: this.formatItemId(node.merchandise.product.id),
							item_category: node.merchandise.product.productType,
							item_variant: node.merchandise.title,
							sku: node.merchandise.sku,
							quantity: node.quantity,
							price,
						});
						if (includeValue) payload.ecommerce.value += price;
					}
				});

			this.dataLayer.push({ ecommerce: null });
			this.dataLayer.push(payload);
		} catch (error) {
			console.log("Error sending dataLayer event: ", error);
		}
	}

	init() {
		Object.values(this.events).forEach(({ selectors, func }) => {
			const listNodes = [];
			selectors.forEach((selector) => {
				listNodes.push(...(Array.from(document.querySelectorAll(selector)) || []));
			});

			if (listNodes && listNodes.length > 0) func(listNodes);
		});
	}
}

window.addEventListener("load", () => {
	if (window.dataLayer) {
		window.GA4DataLayer = new GA4Datalayer(window.dataLayer);
	} else {
		console.warn("GA4Datalayer tracker not loaded");
	}
});
